import { fmt } from "./format/number";
import { rcFrame } from "./geometry/rcCantilever";
import { area, clipBelow, widthAt, type Pt } from "./math/polygon";
import { counterfortGeometry } from "./rc/counterfort";
import type { RcReinforcementItem } from "./rc";
import { DEFAULT_PRICING_NOTE, type Pricing, type PriceItem, type QuantitiesSettings, type QuantityKey, type WallInput } from "./types/input";
import type { WallResult } from "./types/result";

export type QuantityUnit = "m³" | "m²" | "kg" | "t" | "m" | "adet";

export interface QuantityLine {
  key: QuantityKey; label: string; unit: QuantityUnit;
  perMetre: number; total: number;
  /** Hesap açıklaması (rapor "Hesap" sütunu) */
  formula: string;
  /** Bilgi satırı (taş/harç ayrışımı): keşfe girmez */
  info?: boolean;
}
export interface RebarLine {
  position: RcReinforcementItem["position"]; label: string; dia: number;
  /** 1 m duvar için çubuk adedi (kesirli olabilir) ve tek çubuk boyu (m) */
  countPerMetre: number; length: number;
  kgPerMetre: number; total: number;
  note: string;
}
export interface RebarByDia { dia: number; kgPerMetre: number; total: number }
export interface QuantitiesResult {
  structureType: WallResult["structureType"];
  length: number;
  lines: QuantityLine[];
  rebar: { byPosition: RebarLine[]; byDiameter: RebarByDia[]; kgPerMetre: number; total: number } | null;
  assumptions: string[];
}
export interface CostRow { key: QuantityKey; code: string; label: string; unit: string; quantity: number; unitPrice: number; amount: number | null }
export interface CostEstimate { currency: string; note: string; rows: CostRow[]; total: number; priced: boolean; missing: QuantityKey[] }

/** Donatı birim ağırlığı (kg/m): π·φ²/4 · 7850 kg/m³ = 0,006165·φ² */
export const barKgPerMetre = (dia: number): number => 0.006165 * dia * dia;

const r3 = (v: number): number => Math.round(v * 1000) / 1000;

/** Çokgenin y kotundaki en sağ (arka) x'i; kot dışında null. */
function backXAt(poly: Pt[], y: number): number | null { return widthAt(poly, y)?.xMax ?? null; }

/**
 * Arka kazı alanı: y_g (ön zemin) → y_top bandında kazı şevi doğrusu ile duvar arka yüzü arasındaki alan.
 * Arka yüz parça parça doğrusal (köşe kotları kırılma noktası) → yamuk kuralı kesin.
 */
function backCutArea(poly: Pt[], yG: number, yTop: number, xEdge: number, m: number): number {
  if (yTop <= yG + 1e-9) return 0;
  const ys = Array.from(new Set([yG, yTop, ...poly.map((p) => p.y).filter((y) => y > yG + 1e-9 && y < yTop - 1e-9)])).sort((a, b) => a - b);
  const gap = (y: number): number => {
    // köşe kotunda yarı açık kural kenarı kaçırabilir: bir tık içeriden oku
    const xb = backXAt(poly, y) ?? backXAt(poly, y - 1e-6) ?? backXAt(poly, y + 1e-6) ?? xEdge;
    return Math.max(0, xEdge + m * (y - yG) - xb);
  };
  let A = 0;
  for (let i = 0; i + 1 < ys.length; i++) {
    const y0 = ys[i]!, y1 = ys[i + 1]!, e = Math.min(1e-6, (y1 - y0) / 4);
    A += ((gap(y0 + e) + gap(y1 - e)) / 2) * (y1 - y0);
  }
  return A;
}

/** Duvar (taş ya da betonarme) metrajı — birim uzunluk ve toplam. Saf; `analyzeWall` sonucunu kullanır. */
export function computeQuantities(wall: WallInput, result: WallResult): QuantitiesResult {
  const q: QuantitiesSettings = wall.quantities;
  const L = wall.length ?? 10;
  const g = result.geometry;
  const poly = g.polygon;
  // Ön zemin burundan Df yukarıda (eğik tabanda burun topuktan B·tanα yüksek; yatay tabanda toe.y = taban kotu)
  const yG = g.toe.y + g.embedmentDepth, yTop = g.yTop;
  const B = g.B, a = q.workingSpace, m = q.excavationSlope, Df = g.embedmentDepth;
  const sB = g.baseSlope ?? 0;
  const rcg = wall.geometry.mode === "rc-cantilever" ? wall.geometry : null;
  const isRc = !!rcg;
  const f = rcg ? rcFrame(rcg) : null;
  const cf = rcg ? counterfortGeometry(rcg) : null;
  const assumptions: string[] = [];
  const lines: QuantityLine[] = [];
  const line = (key: QuantityKey, label: string, unit: QuantityUnit, perMetre: number, formula: string, info = false): void => {
    lines.push({ key, label, unit, perMetre: r3(perMetre), total: r3(perMetre * L), formula, ...(info ? { info: true } : {}) });
  };

  // ── Kazı ve dolgu
  const keyArea = g.key ? area(g.key) : 0;
  // Eğik tabanda kazı tabanı taban eğimini izler (burundan −a … topuktan +a): ∫(Df + s·x)dx = (B + 2a)·Df + s·B·(B + 2a)/2;
  // yan şevler burundaki Df ile (yaklaşık)
  const pitSlopeExtra = sB > 0 ? (sB * B * (B + 2 * a)) / 2 : 0;
  const pitBelow = (B + 2 * a) * Df + m * Df * Df + pitSlopeExtra;
  const backCut = backCutArea(poly, yG, yTop, g.heel.x + a, m);
  const excavation = pitBelow + backCut + keyArea;
  const wallBelowG = area(clipBelow(poly, yG));
  const backfill = Math.max(0, pitBelow - wallBelowG) + backCut;
  line("excavation", "Kazı (her cins zeminde)", "m³", excavation,
    `Temel kazısı (B + 2a)·D_f + m·D_f²${sB > 0 ? " + s·B·(B + 2a)/2" : ""} = (${fmt(B, 2)} + 2·${fmt(a, 2)})·${fmt(Df, 2)} + ${fmt(m, 2)}·${fmt(Df, 2)}²${sB > 0 ? ` + ${fmt(pitSlopeExtra, 2)}` : ""} = ${fmt(pitBelow, 2)}; arka kazı (şev ${m === 0 ? "dik" : `${fmt(m, 2)}/1`}) ${fmt(backCut, 2)}${keyArea ? `; diş ${fmt(keyArea, 2)}` : ""} m²`);
  line("backfill", "Geri dolgu (kazıdan / seçme malzeme, sıkıştırmalı)", "m³", backfill,
    `Kazı − duvarın zemin altında kalan alanı: (${fmt(pitBelow, 2)} − ${fmt(wallBelowG, 2)}) + ${fmt(backCut, 2)} m²`);
  if (sB > 0) assumptions.push(`Eğik taban (tanα = ${fmt(sB, 3)}, topuk ${fmt(sB * B, 2)} m alçak): kazı tabanı ve grobeton taban eğimini izler; kazı yan şevleri burundaki D_f ile hesaplandı (yaklaşık), grobeton eğik boy B/cosα üzerinden.`);
  assumptions.push(`Kazı: temel altı kotundan; çalışma payı a = ${fmt(a, 2)} m; kazı şevi ${m === 0 ? "dik" : `${fmt(m, 2)} yatay / 1 düşey`}; ön zemin kotu D_f = ${fmt(Df, 2)} m; arka doğal zemin duvar tepesi kotunda kabul edildi (dolgu şevi β kazıda yok sayıldı). Geri dolgu sıkışma payı içermez.`);

  // ── Grobeton
  if (q.blindingThickness > 0) {
    // Eğik tabanda grobeton taban boyunca: B/cosα = B·√(1 + s²)
    const Bb = sB > 0 ? B * Math.sqrt(1 + sB * sB) : B;
    const bl = (Bb + 2 * q.blindingOverhang) * q.blindingThickness;
    line("blinding", "Grobeton (temel altı)", "m³", bl, `(${sB > 0 ? "B/cosα" : "B"} + 2·${fmt(q.blindingOverhang, 2)})·${fmt(q.blindingThickness, 2)} = (${fmt(Bb, 2)} + ${fmt(2 * q.blindingOverhang, 2)})·${fmt(q.blindingThickness, 2)}`);
  }

  // ── Gövde + temel hacmi
  const waste = 1 + q.wasteFactor;
  if (!isRc) {
    const V = g.area * waste;
    line("masonry", "Çimento harçlı moloz taş duvar (gövde + temel)", "m³", V, `Kesit alanı ${fmt(g.area, 3)} m²${q.wasteFactor ? ` × (1 + ${fmt(q.wasteFactor, 2)})` : ""}`);
    line("stone", "  ↳ Ocak taşı (gevşek hacim)", "m³", V * (1 - q.mortarRatio) * q.stoneBulking, `(1 − ${fmt(q.mortarRatio, 2)})·V·${fmt(q.stoneBulking, 2)}`, true);
    line("mortar", "  ↳ Çimento harcı", "m³", V * q.mortarRatio, `${fmt(q.mortarRatio, 2)}·V`, true);
    assumptions.push(`Taş duvar: harç hacim oranı ${fmt(q.mortarRatio, 2)}, ocak taşı kabarma katsayısı ${fmt(q.stoneBulking, 2)} (bilgi satırları; poz duvar hacmi üzerinden).`);
  } else if (f && rcg) {
    const cfArea = cf ? (cf.area * cf.t) / cf.s : 0;
    const V = (g.area + keyArea + cfArea) * waste;
    line("concrete", `Betonarme betonu ${wall.rc.concrete} (gövde + temel${keyArea ? " + diş" : ""}${cf ? " + payanda" : ""})`, "m³", V,
      `Kesit ${fmt(g.area, 3)}${keyArea ? ` + diş ${fmt(keyArea, 3)}` : ""}${cf ? ` + payanda ${fmt(cf.area, 2)}·${fmt(cf.t, 2)}/${fmt(cf.s, 2)} = ${fmt(cfArea, 3)}` : ""} m²${q.wasteFactor ? ` × (1 + ${fmt(q.wasteFactor, 2)})` : ""}`);
    const front = Math.hypot(f.xTopFront - f.xStemFront, f.hs), back = Math.hypot(f.xTopBack - f.xStemBack, f.hs);
    const cfForm = cf ? (2 * cf.area) / cf.s : 0;
    const form = front + back + 2 * f.tf + cfForm;
    line("formwork", "Kalıp (düz yüzeyli betonarme kalıbı)", "m²", form,
      `Gövde ön ${fmt(front, 2)} + arka ${fmt(back, 2)} + taban yanları 2·${fmt(f.tf, 2)}${cf ? ` + payanda 2·${fmt(cf.area, 2)}/${fmt(cf.s, 2)}` : ""}`);
    assumptions.push(`Kalıp: gövde iki yüzü ve taban yan yüzleri; diş zemine dökülür (kalıpsız); payandalı duvarda gövde arka yüzünden payanda kalınlığı düşülmedi.`);
  }

  // ── Donatı
  let rebar: QuantitiesResult["rebar"] = null;
  if (isRc && result.rc && f && rcg) {
    const c = wall.rc.cover;
    const extra = 1 + q.steelExtra;
    const byPosition: RebarLine[] = [];
    const add = (r: RcReinforcementItem, countPerMetre: number, length: number, note: string, dia = r.bar.dia, label = r.label): void => {
      const kg = countPerMetre * length * barKgPerMetre(dia) * extra;
      byPosition.push({ position: r.position, label, dia, countPerMetre: r3(countPerMetre), length: r3(length), kgPerMetre: r3(kg), total: r3(kg * L), note });
    };
    const cap = B - 2 * c;
    for (const r of result.rc.reinforcement) {
      const s = r.bar.spacing, n = s > 0 ? 1 / s : 0;
      switch (r.position) {
        case "stemMain": {
          const full = f.hs + f.tf - 2 * c + r.lb;
          if (r.cutoff) {
            const cut = Math.min(full, r.cutoff.level + r.cutoff.extend - c + r.lb);
            add(r, n / 2, full, "Tam boy: h_s + t_f − 2c + l_b (temele kenetlenme)", r.bar.dia, `${r.label} (tam boy)`);
            add(r, n / 2, cut, `Yarısı y = ${fmt(r.cutoff.level, 2)} + ${fmt(r.cutoff.extend, 2)} m'de kesildi`, r.bar.dia, `${r.label} (kesilen)`);
          } else add(r, n, full, "h_s + t_f − 2c + l_b (temele kenetlenme)");
          break;
        }
        case "stemFront": add(r, n, f.hs + f.tf - 2 * c + r.lb, "h_s + t_f − 2c + l_b"); break;
        case "stemHorizontal": add(r, 2 * (f.hs / s), 1, "İki yüz × (h_s / aralık) adet × 1 m"); break;
        case "stemHorizontalSupport": case "stemHorizontalSpan": add(r, f.hs / s, 1, "(h_s / aralık) adet × 1 m (tek yüz)"); break;
        case "toeMain": add(r, n, Math.min(cap, f.toe + f.tb + r.lb), "L_burun + t_b + l_b (≤ B − 2c)"); break;
        case "heelMain": add(r, n, Math.min(cap, f.heel + f.tb + r.lb), cf ? "Enine şerit: L_topuk + t_b + l_b (≤ B − 2c)" : "L_topuk + t_b + l_b (≤ B − 2c)"); break;
        case "heelLongTop": case "heelLongBottom": add(r, f.heel / s + 1, 1, "(L_topuk / aralık + 1) adet × 1 m (tek yüz)"); break;
        case "baseDistribution": {
          const w = cf ? f.toe + f.tb : cap;
          add(r, 2 * (w / s + 1), 1, cf ? "İki yüz × ((L_burun + t_b) / aralık + 1) × 1 m (topuk boyuna ayrı)" : "İki yüz × ((B − 2c) / aralık + 1) × 1 m");
          break;
        }
        case "keyMain": add(r, n, rcg.key!.depth + rcg.key!.width + r.lb, "d_k + w_k + l_b"); break;
        case "counterfortMain": if (cf) add(r, (r.count ?? 0) / cf.s, Math.hypot(f.heel, f.hs) + r.lb, "adet × (eğik kenar + l_b) / s"); break;
        case "counterfortTieH": if (cf) add(r, (f.hs / s) / cf.s, (f.heel + rcg.counterforts!.topWidth) / 2 + 2 * r.lb, "(h_s / aralık) × (ortalama payanda genişliği + 2·l_b) / s"); break;
        case "counterfortTieV": if (cf) add(r, (f.heel / s) / cf.s, f.hs / 2 + 2 * r.lb, "(L_topuk / aralık) × (h_s/2 + 2·l_b) / s"); break;
      }
    }
    const byDiaMap = new Map<number, number>();
    for (const b of byPosition) byDiaMap.set(b.dia, (byDiaMap.get(b.dia) ?? 0) + b.kgPerMetre);
    const byDiameter: RebarByDia[] = [...byDiaMap.entries()].sort((x, y) => x[0] - y[0]).map(([dia, kg]) => ({ dia, kgPerMetre: r3(kg), total: r3(kg * L) }));
    const kgPerMetre = r3(byDiameter.reduce((s, x) => s + x.kgPerMetre, 0));
    rebar = { byPosition, byDiameter, kgPerMetre, total: r3(kgPerMetre * L) };
    const small = byDiameter.filter((x) => x.dia <= 12).reduce((s, x) => s + x.kgPerMetre, 0);
    const large = kgPerMetre - small;
    if (small > 0) line("rebarSmall", "Nervürlü beton çelik çubuğu φ8–φ12", "kg", small, "Çap bazlı toplamdan");
    if (large > 0) line("rebarLarge", "Nervürlü beton çelik çubuğu φ14–φ28", "kg", large, "Çap bazlı toplamdan");
    assumptions.push(`Donatı boyları şematik: kenetlenme l_b eklenir, pas payı c = ${fmt(c * 1000, 0)} mm düşülür; bindirme + fire payı ${fmt(q.steelExtra * 100, 0)} %. Ağırlık 0,006165·φ² kg/m.`);
  }

  // ── Drenaj
  if (q.drainage.enabled) {
    const hRet = Math.max(0, yTop - yG);
    const rows = Math.max(1, Math.round(hRet / 2));
    line("drainPipe", `Drenaj borusu Ø${fmt(q.drainage.pipeDiameter, 0)} mm (topuk boyunca)`, "m", 1, "Duvar boyunca 1 m/m");
    if (q.drainage.filterThickness > 0) line("filter", "Filtre malzemesi (kırmataş / çakıl, arka yüz)", "m³", q.drainage.filterThickness * hRet, `t_f × (y_üst − y_zemin) = ${fmt(q.drainage.filterThickness, 2)} × ${fmt(hRet, 2)}`);
    line("barbacane", `Barbakan Ø${fmt(q.drainage.barbacaneDiameter, 0)} mm (${rows} sıra, aralık ${fmt(q.drainage.barbacaneSpacing, 1)} m)`, "adet", rows / q.drainage.barbacaneSpacing, `${rows} sıra / ${fmt(q.drainage.barbacaneSpacing, 1)} m`);
    assumptions.push(`Drenaj: boru topuk kotunda duvar boyunca; filtre ön zemin kotundan duvar tepesine arka yüz boyunca ${fmt(q.drainage.filterThickness, 2)} m kalınlıkta; barbakan sıraları ≈ 2 m'de bir.`);
  }
  assumptions.push(`Toplamlar duvar boyu L = ${fmt(L, 1)} m ile; miktarlar kesit geometrisinden (1 m derinlik).`);

  return { structureType: result.structureType, length: L, lines, rebar, assumptions };
}

/**
 * Varsayılan poz tablosu — ÇŞB İnşaat Birim Fiyat Listesi numaralaması (2019 sonrası); poz no ve tanımlar **2020 listesiyle doğrulandı**
 * (`docs/research/08-csb-poz-dogrulama.md`). Fiyatlar boş (0) — güncel yıl listesinden girilir; pozlar yıllara göre değişebilir.
 * Barbakan için birebir poz yok (öneri: 15.315.1002 PVC yağmur borusu, birimi m) → kod boş bırakıldı.
 */
/**
 * Poz numarası, tanım ve birim fiyatlar **ÇŞB Yüksek Fen Kurulu "2026 Temmuz Ayı Güncel Birim Fiyat Listesi"**nden alınmıştır
 * (%25 yüklenici kârı ve genel giderler dâhil, KDV hariç; liste TÜİK endeksleriyle her ay güncellenir → kullanıcı Metraj › Poz
 * tablosundan günceller). Doğrulama: `docs/research/08-csb-poz-dogrulama.md`.
 */
export const DEFAULT_PRICE_ITEMS: PriceItem[] = [
  { key: "excavation", code: "15.120.1001", label: "Makine ile yumuşak ve sert toprak kazılması (serbest kazı)", unit: "", unitPrice: 78.74 },
  { key: "backfill", code: "15.115.1213", label: "Dolguya gelmiş serilmiş her cins kazının (kaya zeminler hariç) elle tokmaklanarak tabaka tabaka sıkıştırılması", unit: "", unitPrice: 295.6 },
  { key: "blinding", code: "15.150.1003", label: "Beton santralinde üretilen veya satın alınan ve beton pompasıyla basılan, C 16/20 basınç dayanım sınıfında, gri renkte, normal hazır beton dökülmesi (beton nakli dahil) — temel altı grobeton", unit: "", unitPrice: 3703.89 },
  { key: "masonry", code: "15.210.1005", label: "Ocak taşı ile 200 dozlu çimento harçlı kargir inşaat yapılması", unit: "", unitPrice: 2754.7 },
  { key: "concrete", code: "15.150.1005", label: "Beton santralinde üretilen veya satın alınan ve beton pompasıyla basılan, C 25/30 basınç dayanım sınıfında, gri renkte, normal hazır beton dökülmesi (beton nakli dahil)", unit: "", unitPrice: 3985.75 },
  { key: "formwork", code: "15.180.1003", label: "Plywood ile düz yüzeyli betonarme kalıbı yapılması", unit: "", unitPrice: 1155.06 },
  { key: "rebarSmall", code: "15.160.1003", label: "Ø 8 - Ø 12 mm nervürlü beton çelik çubuğu, çubukların kesilmesi, bükülmesi ve yerine konulması", unit: "", unitPrice: 52269.43 },
  { key: "rebarLarge", code: "15.160.1004", label: "Ø 14 - Ø 28 mm nervürlü beton çelik çubuğu, çubukların kesilmesi, bükülmesi ve yerine konulması", unit: "", unitPrice: 50247.43 },
  { key: "drainPipe", code: "15.204.1001", label: "Ø 100 mm anma çaplı, PVC esaslı koruge drenaj borusunun temini ve yerine döşenmesi (Ø 125 mm: 15.204.1002)", unit: "", unitPrice: 66.91 },
  { key: "filter", code: "15.125.1006", label: "Çakıl temin edilerek, drenaj yapılması (arka yüz filtre)", unit: "", unitPrice: 1031.24 },
  { key: "barbacane", code: "15.315.1002", label: "Barbakan — Ø 100 mm çapında bir ucu muflu sert PVC yağmur borusu temini ve yerine tesbiti (barbakan için önerilen eşdeğer poz)", unit: "", unitPrice: 313.01 },
];

export const DEFAULT_PRICING_YEAR = "2026 Temmuz (ÇŞB)";
export function makeDefaultPricing(): Pricing { return { currency: "₺", year: DEFAULT_PRICING_YEAR, note: DEFAULT_PRICING_NOTE, items: DEFAULT_PRICE_ITEMS.map((x) => ({ ...x })) }; }

/** Keşif birimi: donatı pozları ton, gerisi metraj birimi. */
export const costUnitOf = (l: QuantityLine): QuantityUnit => (l.unit === "kg" ? "t" : l.unit);

/** Keşif özeti: metraj × birim fiyat (KDV hariç). Fiyatı 0 olan satırın tutarı null; bilgi satırları girmez. */
export function costEstimate(quantities: QuantitiesResult[], pricing: Pricing): CostEstimate {
  const prices = new Map(pricing.items.map((p) => [p.key, p]));
  const acc = new Map<QuantityKey, CostRow>();
  for (const qr of quantities) for (const l of qr.lines) {
    if (l.info) continue;
    const unit = costUnitOf(l), qty = l.unit === "kg" ? l.total / 1000 : l.total;
    const p = prices.get(l.key) ?? DEFAULT_PRICE_ITEMS.find((x) => x.key === l.key);   // eski projede tablo boşsa varsayılan poz/tanım, fiyat 0
    const row = acc.get(l.key) ?? { key: l.key, code: p?.code ?? "", label: p?.label || l.label.trim(), unit: p?.unit || unit, quantity: 0, unitPrice: p?.unitPrice ?? 0, amount: null };
    row.quantity = r3(row.quantity + qty);
    acc.set(l.key, row);
  }
  const rows: CostRow[] = [];
  for (const k of DEFAULT_PRICE_ITEMS.map((x) => x.key)) { const r = acc.get(k); if (r) rows.push(r); }
  for (const r of rows) r.amount = r.unitPrice > 0 ? Math.round(r.quantity * r.unitPrice * 100) / 100 : null;
  const missing = rows.filter((r) => r.amount === null).map((r) => r.key);
  return { currency: pricing.currency, note: pricing.note ?? DEFAULT_PRICING_NOTE, rows, total: Math.round(rows.reduce((s, r) => s + (r.amount ?? 0), 0) * 100) / 100, priced: rows.some((r) => r.amount !== null), missing };
}
